/**
 * TeamSystem Impact - Setup API Client
 * API calls for DMA Setup/Perimeter management
 */

import { apiClient } from '../../../api/client';
import { AxiosError } from 'axios';

/**
 * Helper to extract error message from API response
 */
function extractErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as { error?: string; message?: string };
    return data.error || data.message || defaultMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

// Local type definitions to avoid shared package dependency issues

export type TeamSystemCountry = 'IT' | 'ES' | 'FR' | 'CH' | 'AL' | 'DK' | 'TR' | 'IL';
export type ValueChainPosition = 'upstream' | 'operations' | 'downstream';
export type StakeholderCategory =
  | 'employees'
  | 'customers'
  | 'suppliers'
  | 'investors'
  | 'local_communities'
  | 'regulators'
  | 'partners'
  | 'other';
export type DMASetupStatus = 'draft' | 'active' | 'archived';

export interface GroupCompany {
  id: string;
  name: string;
  country: TeamSystemCountry;
  isConsolidated: boolean;
  consolidationPercentage?: number;
  naceCodes?: string[];
  employeeCount?: number;
  revenue?: number;
  isParent?: boolean;
  consolidationType?: 'full' | 'proportional' | 'equity' | 'excluded';
  isInScope?: boolean;
}

export interface StakeholderCategoryConfig {
  category: StakeholderCategory;
  priority: 'high' | 'medium' | 'low';
  engagementMethods: string[];
  keyTopics?: string[];
  lastEngagementDate?: string;
}

// Frontend format (array-based, used in wizard)
export interface ValueChainConfigItem {
  position: ValueChainPosition;
  description?: string;
  keyActivities: string[];
  geographies: TeamSystemCountry[];
  materialTopics?: string[];
}

// Backend format (object-based, returned from API)
export interface ValueChainConfigObject {
  upstream: { enabled: boolean; description?: string; categories: string[] };
  operations: { enabled: boolean; description?: string; categories: string[] };
  downstream: { enabled: boolean; description?: string; categories: string[] };
}

// Support both formats
export type ValueChainConfig = ValueChainConfigItem[] | ValueChainConfigObject;

export interface NACECode {
  code: string;
  description: string;
  isPrimary?: boolean;
  revenuePercentage?: number;
  division?: string;
  section?: string;
  isEligible?: boolean;
}

export interface DMASetup {
  _id?: string;
  aziendaId: string;
  assessmentYear: number;
  status: DMASetupStatus;
  groupCompanies: GroupCompany[];
  activeCountries: TeamSystemCountry[];
  valueChainConfig: ValueChainConfig[];
  stakeholderCategories: StakeholderCategoryConfig[];
  naceCodes: NACECode[];
  materialityThresholds?: {
    impactScore: number;
    financialScore: number;
  };
  notes?: string;
  analysisImage?: string | null;
  methodologyImage?: string | null;
  yearComparisonImage?: string | null;
  newIrosImage?: string | null;
  createdBy: string;
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDMASetupDTO {
  assessmentYear: number;
  groupCompanies?: GroupCompany[];
  activeCountries?: TeamSystemCountry[];
  valueChainConfig?: ValueChainConfig[];
  stakeholderCategories?: StakeholderCategoryConfig[];
  naceCodes?: NACECode[];
  materialityThresholds?: {
    impactScore: number;
    financialScore: number;
  };
  notes?: string;
  analysisImage?: string | null;
  methodologyImage?: string | null;
  yearComparisonImage?: string | null;
  newIrosImage?: string | null;
}

export interface UpdateDMASetupDTO extends Partial<CreateDMASetupDTO> {
  status?: DMASetupStatus;
}

const BASE_PATH = '/dma/setup';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface CompletenessStats {
  percentage: number;
  details: {
    groupCompanies: boolean;
    activeCountries: boolean;
    valueChain: boolean;
    stakeholders: boolean;
    naceCodes: boolean;
  };
}

export const setupApi = {
  create: async (data: CreateDMASetupDTO): Promise<DMASetup> => {
    try {
      const response = await apiClient.post<ApiResponse<DMASetup>>(`${BASE_PATH}`, data);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to create setup');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la creazione del setup'));
    }
  },

  list: async (): Promise<DMASetup[]> => {
    const response = await apiClient.get<ApiResponse<DMASetup[]>>(`${BASE_PATH}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list setups');
    return response.data.data!;
  },

  getActive: async (): Promise<DMASetup | null> => {
    try {
      const response = await apiClient.get<ApiResponse<DMASetup>>(`${BASE_PATH}/active`);
      if (!response.data.success) return null;
      return response.data.data!;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  getByYear: async (year: number): Promise<DMASetup | null> => {
    try {
      const response = await apiClient.get<ApiResponse<DMASetup>>(`${BASE_PATH}/year/${year}`);
      if (!response.data.success) return null;
      return response.data.data!;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  getById: async (id: string): Promise<DMASetup> => {
    const response = await apiClient.get<ApiResponse<DMASetup>>(`${BASE_PATH}/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Setup not found');
    return response.data.data!;
  },

  update: async (id: string, data: UpdateDMASetupDTO): Promise<DMASetup> => {
    try {
      const response = await apiClient.put<ApiResponse<DMASetup>>(`${BASE_PATH}/${id}`, data);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to update setup');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'aggiornamento del setup'));
    }
  },

  activate: async (id: string): Promise<DMASetup> => {
    const response = await apiClient.post<ApiResponse<DMASetup>>(`${BASE_PATH}/${id}/activate`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to activate setup');
    return response.data.data!;
  },

  archive: async (id: string): Promise<DMASetup> => {
    const response = await apiClient.post<ApiResponse<DMASetup>>(`${BASE_PATH}/${id}/archive`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to archive setup');
    return response.data.data!;
  },

  clone: async (id: string, newYear: number): Promise<DMASetup> => {
    const response = await apiClient.post<ApiResponse<DMASetup>>(`${BASE_PATH}/${id}/clone`, { newYear });
    if (!response.data.success) throw new Error(response.data.error || 'Failed to clone setup');
    return response.data.data!;
  },

  cloneYear: async (
    sourceYear: number,
    targetYear: number,
    options: {
      dmaSetup?: boolean;
      iros?: boolean;
      campaigns?: boolean;
      externalStakeholders?: boolean;
      internalStakeholders?: boolean;
      assessments?: boolean;
      documents?: boolean;
    }
  ): Promise<{
    setup: DMASetup;
    irosCloned: number;
    irosSkipped: Array<{ code: string; reason: string }>;
  }> => {
    const response = await apiClient.post<
      ApiResponse<DMASetup> & {
        irosCloned?: number;
        irosSkipped?: Array<{ code: string; reason: string }>;
      }
    >(`${BASE_PATH}/clone-year`, {
      sourceYear,
      targetYear,
      options,
    });
    if (!response.data.success) throw new Error(response.data.error || 'Failed to clone year');
    return {
      setup: response.data.data!,
      irosCloned: response.data.irosCloned ?? 0,
      irosSkipped: response.data.irosSkipped ?? [],
    };
  },

  getCompleteness: async (id: string): Promise<CompletenessStats> => {
    const response = await apiClient.get<ApiResponse<CompletenessStats>>(`${BASE_PATH}/${id}/completeness`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get completeness');
    return response.data.data!;
  },

  addGroupCompany: async (setupId: string, company: GroupCompany): Promise<DMASetup> => {
    const response = await apiClient.post<ApiResponse<DMASetup>>(`${BASE_PATH}/${setupId}/companies`, company);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add company');
    return response.data.data!;
  },

  removeGroupCompany: async (setupId: string, companyId: string): Promise<DMASetup> => {
    const response = await apiClient.delete<ApiResponse<DMASetup>>(`${BASE_PATH}/${setupId}/companies/${companyId}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to remove company');
    return response.data.data!;
  },

  addStakeholderCategory: async (setupId: string, category: StakeholderCategoryConfig): Promise<DMASetup> => {
    const response = await apiClient.post<ApiResponse<DMASetup>>(`${BASE_PATH}/${setupId}/stakeholder-categories`, category);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add stakeholder category');
    return response.data.data!;
  },

  addNACECode: async (setupId: string, naceCode: NACECode): Promise<DMASetup> => {
    const response = await apiClient.post<ApiResponse<DMASetup>>(`${BASE_PATH}/${setupId}/nace-codes`, naceCode);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add NACE code');
    return response.data.data!;
  },
};
